<script setup lang="ts">
import searchIcon from '#core/assets/img/icon/search-icon.svg';

interface Props {
  placeholder?: string;
  variant?: 'outlined' | 'plain' | 'filled' | 'underlined' | 'solo';
  density?: 'default' | 'comfortable' | 'compact';
  prependInnerIcon?: string;
  class?: string;
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: "e.g. 'Backyard levelling'",
  variant: 'plain',
  density: 'compact',
  prependInnerIcon: undefined,
  class: '',
});

const searchQuery = defineModel<string | null>();
const emit = defineEmits<{
  search: [];
  removeHistory: [query: string];
}>();

// Recent searches management
const RECENT_SEARCHES_KEY = 'recentSearches';
const MAX_RECENT_SEARCHES = 5;

const recentSearches = ref<string[]>([]);
const isMenuOpen = ref(false);
const inputValue = ref('');
const userIsTyping = ref(false); // Track if user is actively typing

// Load recent searches from localStorage
const loadRecentSearches = () => {
  const stored = localStorage.getItem(RECENT_SEARCHES_KEY);
  if (stored) {
    recentSearches.value = JSON.parse(stored);
  }
};

// Load on mount
onMounted(() => {
  loadRecentSearches();
});

// Watch searchQuery from parent (programmatic updates, not user typing)
watch(
  () => searchQuery.value,
  (newVal) => {
    if (newVal !== inputValue.value && !userIsTyping.value) {
      inputValue.value = newVal || '';
    }
  }
);

// Filtered searches based on input
const filteredSearches = computed(() => {
  // Don't show if input is empty or user is not typing
  if (!inputValue.value || inputValue.value.trim() === '' || !userIsTyping.value) {
    return [];
  }

  const query = inputValue.value.toLowerCase();
  return recentSearches.value.filter((search) => search.toLowerCase().includes(query));
});

// Show menu only if there are filtered results and user is actively typing
watch([inputValue, filteredSearches], () => {
  isMenuOpen.value = userIsTyping.value && inputValue.value.trim() !== '' && filteredSearches.value.length > 0;
});

// Handle input change
const handleInput = (value: string) => {
  userIsTyping.value = true; // User is actively typing

  // Reload recent searches to pick up any new additions
  loadRecentSearches();

  inputValue.value = value;
  searchQuery.value = value;

  // Close menu if input is empty
  if (!value || value.trim() === '') {
    isMenuOpen.value = false;
  }
};

// Handle selecting a search history item
const selectSearch = (query: string) => {
  userIsTyping.value = false; // Not typing anymore, selected from history
  inputValue.value = query;
  searchQuery.value = query;
  isMenuOpen.value = false;
};

// Remove a search history item
const removeSearchItem = (query: string) => {
  recentSearches.value = recentSearches.value.filter((s) => s !== query);
  localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(recentSearches.value));
  emit('removeHistory', query);
};

// Handle focus
const handleFocus = () => {
  // Don't show menu on focus, only when user starts typing
  // This prevents the menu from showing when page loads with pre-filled value
  isMenuOpen.value = false;
  userIsTyping.value = false;
  stopPlaceRotate();
};

// Handle blur
const handleBlur = () => {
  // Delay to allow click events on menu items

  startPlaceholderRotate();
  setTimeout(() => {
    isMenuOpen.value = false;
    userIsTyping.value = false; // Reset typing state
  }, 200);
};

// Handle clear
const handleClear = () => {
  inputValue.value = '';
  searchQuery.value = null;
  userIsTyping.value = false;
  isMenuOpen.value = false;
  startPlaceholderRotate();
};

const {
  placeholder,
  start: startPlaceholderRotate,
  stop: stopPlaceRotate,
} = useRotatingPlaceholder(
  [
    'e.g. “Backyard levelling”',
    'e.g. “Need a Tractor”',
    'e.g. “Looking for Auto Electrician”',
    'e.g. “Looking for Auto Electrician”',
  ],
  searchQuery
);
</script>

<template>
  <div class="search-history-autocomplete">
    <v-text-field
      :model-value="inputValue"
      :placeholder="placeholder"
      :variant="variant"
      :density="density"
      :prepend-inner-icon="prependInnerIcon"
      :class="class"
      hide-details
      autocomplete="off"
      clearable
      @update:model-value="handleInput"
      @click:clear="handleClear"
      @focus="handleFocus"
      @blur="handleBlur"
      @keydown.enter="
        userIsTyping = false;
        emit('search');
      "
    >
      <template #prepend-inner>
        <img :src="searchIcon" alt="Search" class="search-icon" />
      </template>
    </v-text-field>

    <!-- Custom Dropdown Menu -->
    <v-menu
      v-model="isMenuOpen"
      activator="parent"
      :close-on-content-click="false"
      :offset="6"
      width="394px"
      :disabled="!filteredSearches.length || !userIsTyping || inputValue.trim() === ''"
    >
      <v-card
        v-if="filteredSearches.length > 0 && userIsTyping && inputValue.trim() !== ''"
        class="search-history-menu"
        elevation="8"
      >
        <div class="search-history-menu__header">RECENT SEARCHES</div>

        <v-list class="search-history-menu__list">
          <v-list-item
            v-for="(search, index) in filteredSearches"
            :key="index"
            class="search-history-menu__item"
            @click="selectSearch(search)"
          >
            <template #prepend>
              <v-icon class="search-history-menu__clock-icon">mdi-clock-outline</v-icon>
            </template>

            <v-list-item-title class="search-history-menu__text">
              {{ search }}
            </v-list-item-title>

            <template #append>
              <v-btn
                icon
                variant="text"
                size="small"
                class="search-history-menu__remove-btn"
                @click.stop="removeSearchItem(search)"
              >
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </template>
          </v-list-item>
        </v-list>
      </v-card>
    </v-menu>
  </div>
</template>

<style scoped lang="scss">
.search-history-autocomplete {
  position: relative;
  width: 100%;

  // Style placeholder text to be black
  :deep(.v-field__input::placeholder) {
    color: #000000 !important;
    opacity: 1 !important;
  }

  :deep(.v-field__input input::placeholder) {
    color: #000000 !important;
    opacity: 1 !important;
  }
}

.search-icon {
  width: 24px;
  height: 24px;
}

.search-history-menu {
  &__header {
    padding: 12px 24px;
    font-size: 14px;
    font-weight: 700;
    letter-spacing: 0.5px;
    color: rgba(0, 0, 0, 0.87);
  }

  &__list {
    padding: 0;
  }

  &__item {
    min-height: 56px !important;
    padding: 6px 24px !important;
    cursor: pointer;

    &:hover {
      background-color: rgba(0, 0, 0, 0.04);
    }
  }

  &__clock-icon {
    color: rgba(0, 0, 0, 0.54);
    margin-right: 16px;
  }

  &__text {
    font-size: 16px;
    color: rgba(0, 0, 0, 0.87);
  }

  &__remove-btn {
    opacity: 0.6;

    &:hover {
      opacity: 1;
    }
  }
}
</style>
